import { cn } from '@/lib/utils';
import { ChevronDown, Filter, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { FluidHoverGroup, useFluidHoverGroupItem } from '@/components/ui/fluid-hover-group';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';

import { Button } from '../ui/button';

export interface JobFilterOption {
    id: string;
    name: string;
    color: string;
}

interface JobFilterControlProps {
    options: JobFilterOption[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

const PILL =
    'px-4 py-1.5 border rounded-full font-medium text-sm transition-colors';
const PILL_ACTIVE = 'bg-muted text-foreground';

export function JobFilterControl({
    options,
    selectedIds,
    onChange
}: JobFilterControlProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const sorted = useMemo(
        () =>
            [...options].sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            ),
        [options]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q
            ? sorted.filter((o) => o.name.toLowerCase().includes(q))
            : sorted;
    }, [sorted, query]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const toggle = (id: string) => {
        onChange(
            selectedSet.has(id)
                ? selectedIds.filter((x) => x !== id)
                : [...selectedIds, id]
        );
    };

    const clearAll = () => {
        onChange([]);
        setOpen(false);
        setQuery('');
    };

    const selectAllFiltered = () => {
        const merged = new Set(selectedIds);
        filtered.forEach((o) => merged.add(o.id));
        onChange(Array.from(merged));
    };

    const soloOption =
        selectedIds.length === 1
            ? options.find((o) => o.id === selectedIds[0])
            : undefined;
    const chipLabel =
        selectedIds.length === 1
            ? soloOption?.name ?? ''
            : `${selectedIds.length} selected`;

    return (
        <div className="flex items-center gap-2">
            <Button pill
                onClick={clearAll}
                variant={selectedIds.length === 0 ? 'secondary' : 'tertiary'}
               
            >
                All
            </Button>

            <Popover
                open={open}
                onOpenChange={(next) => {
                    setOpen(next);
                    if (!next) setQuery('');
                }}
            >
                <PopoverTrigger asChild>
                    <Button pill
                        className="px-3 py-4 text-sm"
                        variant={'tertiary'}
                        size={'default'}
                    >
                        <Filter />
                        Filter jobs
                        <ChevronDown
                            className={cn(
                                'transition-transform',
                                open && 'rotate-180'
                            )}
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="bg-background p-0 rounded-xl w-80 overflow-hidden"
                >
                    <div className="relative border-border border-b">
                        <Search
                            strokeWidth={1.5}
                            className="top-1/2 left-4 absolute size-5 text-muted-foreground -translate-y-1/2"
                        />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search jobs…"
                            className="bg-transparent pr-4 pl-11 outline-hidden w-full h-12 placeholder:text-muted-foreground text-(length:--fs-subtitle)"
                        />
                    </div>
                    <div className="flex justify-between items-center py-2 pr-2 pl-4">
                        <span className="text-muted-foreground text-sm">
                            {filtered.length} jobs, A–Z
                        </span>
                        <span className="flex items-center gap-1">
                            <Button
                                onClick={selectAllFiltered}
                                variant="ghost"
                                size="compact"
                            >
                                Select all
                            </Button>
                            <Button
                                onClick={clearAll}
                                variant="ghost"
                                size="compact"
                            >
                                Clear
                            </Button>
                        </span>
                    </div>
                    {filtered.length === 0 ? (
                        <p className="py-6 text-muted-foreground text-sm text-center">
                            No jobs match "{query}"
                        </p>
                    ) : (
                        <FluidHoverGroup className="p-1.5 pt-0 max-h-72 overflow-y-auto">
                            {filtered.map((option) => (
                                <JobFilterRow
                                    key={option.id}
                                    option={option}
                                    checked={selectedSet.has(option.id)}
                                    onToggle={() => toggle(option.id)}
                                />
                            ))}
                        </FluidHoverGroup>
                    )}
                </PopoverContent>
            </Popover>

            {selectedIds.length > 0 && (
                <span
                    className={cn(
                        PILL,
                        'inline-flex items-center gap-2',
                        PILL_ACTIVE,
                        'pr-2'
                    )}
                >
                    {chipLabel}
                    <Button pill
                        onClick={clearAll}
                        variant="ghost"
                        size="icon-compact"
                        className="size-5"
                    >
                        <X />
                    </Button>
                </span>
            )}
        </div>
    );
}

interface JobFilterRowProps {
    option: JobFilterOption;
    checked: boolean;
    onToggle: () => void;
}

function JobFilterRow({ option, checked, onToggle }: JobFilterRowProps) {
    const itemRef = useFluidHoverGroupItem<HTMLButtonElement>(undefined);

    return (
        <button
            ref={itemRef}
            type="button"
            onClick={onToggle}
            className="flex items-center gap-3 px-2.5 py-2 rounded-lg w-full text-left"
        >
            <div
                className="flex justify-center items-center rounded-md size-5 font-semibold text-[10px] text-white shrink-0"
                style={{ backgroundColor: option.color }}
            >
                {option.name[0].toUpperCase()}
            </div>
            <span className="flex-1 text-(length:--fs-subtitle) truncate">
                {option.name}
            </span>
            <Checkbox
                checked={checked}
                tabIndex={-1}
                className="rounded-[5px] size-5 pointer-events-none"
                iconClassName="size-3.5"
            />
        </button>
    );
}
